import React, { Component } from 'react';
import _AdminService from 'modules/AdminService';
import _PublicationService from 'modules/PublicationService';
import _notification from 'modules/notification';
import _utils from 'modules/Utils';
import _branding from 'modules/branding';
import { buildEligibleCandidateListPdf } from 'modules/EligibleCandidateListPdf';
import { Card, Modal } from 'antd';
import PageHeader from 'components/enterprise/PageHeader';
import LoadingSpinner from 'components/enterprise/LoadingSpinner';
import EmptyState from 'components/enterprise/EmptyState';
import DropdownList from 'components/controls/DropdownList';
import ButtonComponent from 'components/controls/Button';
import ConfirmPopup from '../popup/ConfirmPopup';
import { routesAdmin } from 'routes/Routes';
import { GENERAL_ERROR_MESSAGE } from 'constants/constants';
import moment from 'moment';

// Recruitment Candidate-List Publication Modernization phase.
//
// Replaces the current manual "Excel -> hand-convert to PDF -> copy to server -> hard-bind in frontend"
// production workflow for Eligible Candidate Lists with:
//   Authoritative candidate data -> Eligibility query -> Post-wise PDF -> Preview -> Publish
// reusing the existing Publication module end-to-end (Create/Supersede/Publish, Draft/Published/Archived,
// audit log) — no parallel publication subsystem, no new schema for "is this candidate eligible" (that
// remains Student.Is_Submitted=1 AND Is_Approved_Rejected=1, computed entirely server-side).
//
// First vs Final is not a new flag anywhere — it is derived purely from whether a Published, active
// Eligible Candidates Publication already exists for the selected post (matched by Title, since
// Publication has no PostId column and adding one isn't justified just to label a stage). If none
// exists yet, generating produces the First List (Create -> Draft, then an explicit Publish click).
// If one already exists, generating produces the Final List (Supersede, which publishes the
// replacement AND archives the previous version in one atomic admin action) — the eligibility query
// itself is identical either way; only the label and the publish mechanism differ.
const PUBLICATION_TYPE_ELIGIBLE = 'EligibleCandidatesExamination';

const titleForPost = (postName) => 'Eligible Candidates List - ' + postName;

class EligibleCandidateList_new extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            generating: false,
            publishing: false,
            posts: [],
            publications: [],
            portalConfig: {},
            selectedPostId: null,
            candidates: null,
            generatedAt: null,
            pdfBlob: null,
            pdfFileName: '',
            showPublishConfirm: false,
        };
    }

    componentDidMount() {
        this.loadInitial();
    }

    loadInitial = async () => {
        this.setState({ loading: true });
        try {
            const [postsResp, publicationsResp, portalConfigResp] = await Promise.all([
                _AdminService.GetAllPost(),
                _PublicationService.getAll(),
                _AdminService.GetPortalConfigurationWithoutImage()
            ]);
            this.setState({
                posts: postsResp.data || [],
                publications: publicationsResp.data || [],
                portalConfig: portalConfigResp.data || {},
                loading: false
            });
        } catch (e) {
            this.setState({ loading: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    refreshPublications = async () => {
        try {
            const response = await _PublicationService.getAll();
            this.setState({ publications: response.data || [] });
        } catch (e) { /* non-fatal; existing list stays stale until next reload */ }
    }

    getSelectedPost = () => (this.state.posts || []).find(p => p.Id === this.state.selectedPostId) || null;

    // The Published, active Eligible Candidates record for the currently-selected post, if any.
    getExistingPublication = () => {
        const post = this.getSelectedPost();
        if (!post) return null;
        const title = titleForPost(post.Name);
        return (this.state.publications || []).find(p =>
            p.PublicationType === PUBLICATION_TYPE_ELIGIBLE &&
            p.Title === title &&
            p.Status === 'Published' &&
            p.IsActive
        ) || null;
    }

    getStage = () => (this.getExistingPublication() ? 'Final' : 'First');

    onPostChange = (event) => {
        // The underlying <select> always yields a string value; Post.Id from the API is numeric —
        // without this parse, every strict === comparison against posts/publications below would
        // silently fail to match.
        const postId = event.target.value ? parseInt(event.target.value, 10) : null;
        this.setState({
            selectedPostId: postId,
            candidates: null,
            pdfBlob: null,
            pdfFileName: '',
            generatedAt: null
        });
    }

    handleGenerateAndPreview = async () => {
        const post = this.getSelectedPost();
        if (!post) {
            _notification.showMessage('error', 'Please select a Post first.');
            return;
        }

        // Opened synchronously, still inside the click's user-gesture window — a window.open() called
        // only after an awaited API response below is otherwise silently blocked by Chrome's popup
        // blocker (it no longer counts as a direct response to user input). Navigated to the real blob
        // URL once the PDF is ready; closed instead if generation turns out to have nothing to show.
        const previewWindow = window.open('', '_blank');

        this.setState({ generating: true });
        try {
            const response = await _AdminService.GetEligibleCandidates(post.Id);
            const candidates = response.data || [];
            if (candidates.length === 0) {
                if (previewWindow) { previewWindow.close(); }
                this.setState({ generating: false, candidates: [], pdfBlob: null });
                _notification.showMessage('error', 'No eligible candidates found for this post (Submitted + Approved). Nothing to publish yet.');
                return;
            }

            const generatedAt = new Date();
            const stage = this.getStage();
            const entityName = _branding.resolveOrganizationName(this.state.portalConfig.Exam_Name || '');
            const doc = buildEligibleCandidateListPdf({
                entityName,
                examName: this.state.portalConfig.Exam_Name,
                postName: post.Name,
                stage,
                candidates,
                generatedAt
            });
            const pdfBlob = doc.output('blob');
            const pdfFileName = _utils.buildEligibleCandidateListFileName(stage, post.Name, generatedAt);

            // Preview (required step before Publish, never auto-published from here) — the same
            // "View PDF" pattern PublicationMaster_new.jsx already uses for published documents, just
            // navigating the already-open window rather than opening a fresh one.
            const previewUrl = URL.createObjectURL(pdfBlob);
            if (previewWindow) {
                previewWindow.location.href = previewUrl;
            } else {
                // Popup blocked even on the synchronous open — fall back and let the browser's own
                // blocked-popup UI (if any) surface to the admin rather than losing the preview silently.
                window.open(previewUrl, '_blank');
            }

            this.setState({ generating: false, candidates, pdfBlob, pdfFileName, generatedAt });
            _notification.showMessage('success', `${stage} Eligible Candidates List generated for ${post.Name} (${candidates.length} candidate(s)). Review the preview, then Publish when ready.`);
        } catch (e) {
            if (previewWindow) { previewWindow.close(); }
            this.setState({ generating: false });
            console.log('Error: ', e);
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    handleRePreview = () => {
        if (!this.state.pdfBlob) return;
        const url = URL.createObjectURL(this.state.pdfBlob);
        window.open(url, '_blank');
    }

    openPublishConfirm = () => {
        if (!this.state.pdfBlob || !this.state.candidates || this.state.candidates.length === 0) {
            _notification.showMessage('error', 'Generate and preview the list before publishing.');
            return;
        }
        this.setState({ showPublishConfirm: true });
    }

    handlePublish = async () => {
        this.setState({ showPublishConfirm: false, publishing: true });
        const post = this.getSelectedPost();
        const stage = this.getStage();
        const existing = this.getExistingPublication();

        try {
            const file = new File([this.state.pdfBlob], this.state.pdfFileName, { type: 'application/pdf' });
            const model = {
                PublicationType: PUBLICATION_TYPE_ELIGIBLE,
                Title: titleForPost(post.Name),
                Description: `${stage} Eligible Candidates List for ${post.Name} — generated ${moment(this.state.generatedAt).format('DD-MMM-YYYY HH:mm')} ` +
                    `from ${this.state.candidates.length} submitted-and-approved candidate(s)` +
                    (stage === 'Final' ? ' (freshly re-evaluated after the reconciliation window; supersedes the First Eligible List).' : '.'),
                PublicationLanguage: 'en',
                PublicationDate: moment().format('YYYY-MM-DDT00:00:00'),
                EffectiveDate: '',
                DisplayOrder: 0
            };

            let response;
            if (stage === 'Final' && existing) {
                // One atomic admin action: publishes the new record AND archives the previous
                // (First) version — the existing Publication chain-of-record mechanism, not a new one.
                response = await _PublicationService.supersede(existing.Id, model, file);
            } else {
                response = await _PublicationService.create(model, file);
                if (response.status === 200 && response.data && response.data.Id) {
                    response = await _PublicationService.publish(response.data.Id);
                }
            }

            if (response.status === 200) {
                _notification.showMessage('success', `${stage} Eligible Candidates List published for ${post.Name}.`);
                this.setState({ publishing: false, candidates: null, pdfBlob: null, pdfFileName: '', generatedAt: null });
                this.refreshPublications();
            } else {
                this.setState({ publishing: false });
                _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ publishing: false });
            console.log('Error: ', e);
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    renderReconciliationInfo() {
        const { portalConfig } = this.state;
        if (!portalConfig.Registration_Cutoff_DateTime) {
            return null;
        }
        const cutoff = moment(portalConfig.Registration_Cutoff_DateTime);
        const windowDays = portalConfig.Reconciliation_Window_Days;
        return (
            <div className="mb-3" style={{ background: '#f5f7fa', padding: '10px 14px', borderRadius: 4, fontSize: 13 }}>
                Registration closed/closes: <b>{cutoff.isValid() ? cutoff.format('DD-MMM-YYYY hh:mm A') + ' IST' : '-'}</b>
                {windowDays ? (
                    <>
                        &nbsp;&middot;&nbsp;Reconciliation window: <b>{windowDays} day(s)</b>
                        &nbsp;(Final Eligible List is typically generated on/after{' '}
                        <b>{cutoff.clone().add(windowDays, 'days').format('DD-MMM-YYYY')}</b>)
                    </>
                ) : null}
            </div>
        );
    }

    renderStatus() {
        const post = this.getSelectedPost();
        if (!post) return null;
        const existing = this.getExistingPublication();
        const stage = this.getStage();
        return (
            <div className="mb-3" style={{ fontSize: 13 }}>
                {existing ? (
                    <span>
                        An <b>Eligible Candidates List</b> is already published for <b>{post.Name}</b>
                        {existing.PublicationDate ? (' (' + moment(existing.PublicationDate).format('DD-MMM-YYYY') + ')') : ''}.
                        Generating now will freshly re-evaluate eligibility and publish the
                        <b> Final Eligible Candidates List</b>, which supersedes and archives that version.
                    </span>
                ) : (
                    <span>No Eligible Candidates List has been published yet for <b>{post.Name}</b>. Generating now will produce the <b>First Eligible Candidates List</b>.</span>
                )}
                <div>Next action for this post: <b>{stage} Eligible Candidates List</b></div>
            </div>
        );
    }

    render() {
        const { loading, posts, selectedPostId, candidates, pdfBlob, generating, publishing } = this.state;

        if (loading) {
            return <LoadingSpinner />;
        }

        return (
            <div className="container-fluid p-3">
                <PageHeader
                    title="Eligible Candidate List"
                    breadcrumbItems={[{ label: 'Dashboard', path: routesAdmin.dashboard.path }, { label: 'Candidates' }, { label: 'Eligible Candidate List' }]}
                />
                <div className="max1200 m-auto">
                    <Card>
                        {this.renderReconciliationInfo()}

                        {posts.length === 0 ? (
                            <EmptyState icon="team" title="No Posts Found" description="Create a Post/Exam before generating an Eligible Candidate List." />
                        ) : (
                            <>
                                <div className="row">
                                    <div className="col-sm-6 mb-3">
                                        <DropdownList
                                            value={selectedPostId || 0}
                                            name="postId"
                                            onChange={this.onPostChange}
                                            placeholder="Select Post"
                                            heading="Post/Exam"
                                            className="w-100"
                                            wrapClass="wrap"
                                            isRequired={true}
                                            options={posts}
                                        />
                                    </div>
                                </div>

                                {this.renderStatus()}

                                <div className="mb-3">
                                    <ButtonComponent
                                        type="button"
                                        value={generating ? 'Generating…' : 'Generate & Preview'}
                                        className="button-global button-primary autoHeight small mr-3"
                                        onClick={this.handleGenerateAndPreview}
                                        disabled={!selectedPostId || generating}
                                    />
                                    {pdfBlob && (
                                        <>
                                            <ButtonComponent
                                                type="button"
                                                value="Re-open Preview"
                                                className="button-global button-border autoHeight small mr-3"
                                                onClick={this.handleRePreview}
                                            />
                                            <ButtonComponent
                                                type="button"
                                                value={publishing ? 'Publishing…' : `Publish (${this.getStage()} Eligible List)`}
                                                className="button-global button-primary autoHeight small"
                                                onClick={this.openPublishConfirm}
                                                disabled={publishing}
                                            />
                                        </>
                                    )}
                                </div>

                                {candidates && candidates.length > 0 && (
                                    <div style={{ fontSize: 13 }}>
                                        Generated list contains <b>{candidates.length}</b> eligible candidate(s)
                                        (Submitted + Approved) for <b>{this.getSelectedPost() && this.getSelectedPost().Name}</b>,
                                        not yet published.
                                    </div>
                                )}
                            </>
                        )}
                    </Card>
                </div>

                <Modal
                    centered={true}
                    width={440}
                    className="modelPopAnt admin-home"
                    visible={this.state.showPublishConfirm}
                    onCancel={() => this.setState({ showPublishConfirm: false })}
                    footer={false}
                >
                    <ConfirmPopup
                        onCancel={() => this.setState({ showPublishConfirm: false })}
                        onOk={this.handlePublish}
                        message={`Publish the ${this.getStage()} Eligible Candidates List for ${this.getSelectedPost() ? this.getSelectedPost().Name : ''}? This will make it visible to candidates on the portal home page immediately.`}
                    />
                </Modal>
            </div>
        );
    }
}

export default EligibleCandidateList_new;
